import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "toolbar", "selectedCount", "confirmModal", 
    "confirmForm", "idsInput", "confirmTitle", 
    "confirmMessage", "confirmButton", "methodInput",
    "dynamicFields", 
    // Legacy targets for backward compatibility
    "assignRoleModal", "assignRoleForm", "userIdsInput", "actionInput"
  ]

  connect() {
    this.selectedIds = new Set()
    // Legacy support
    this.selectedUsers = this.selectedIds
    this.updateToolbar()
  }

  // Called when checkbox selection changes
  selectionChanged(event) {
    const checkbox = event.target
    const id = checkbox.value
    
    if (checkbox.checked) {
      this.selectedIds.add(id)
    } else {
      this.selectedIds.delete(id)
    }
    
    this.updateToolbar()
  }

  // Called when "select all" is toggled
  selectAll(event) {
    const selectAllCheckbox = event.target
    const allCheckboxes = document.querySelectorAll('[data-select-all-target="checkbox"]')
    
    allCheckboxes.forEach(checkbox => {
      checkbox.checked = selectAllCheckbox.checked
      const id = checkbox.value
      
      if (selectAllCheckbox.checked) {
        this.selectedIds.add(id)
      } else {
        this.selectedIds.delete(id)
      }
    })
    
    this.updateToolbar()
  }

  clearSelection() {
    this.selectedIds.clear()
    
    // Uncheck all checkboxes
    const allCheckboxes = document.querySelectorAll('[data-select-all-target="checkbox"], [data-select-all-target="checkboxAll"]')
    allCheckboxes.forEach(checkbox => {
      checkbox.checked = false
    })
    
    this.updateToolbar()
  }

  selectAction(event) {
    const action = event.params.action
    const formUrl = event.params.formUrl
    const method = event.params.method || 'post'
    const isDangerous = event.params.dangerous === "true"
    const requiresModal = event.params.requiresModal === "true"
    
    if (this.selectedIds.size === 0) {
      alert("Please select at least one item.")
      return
    }

    // Update IDs in all forms (both new and legacy)
    this.updateIdsInput()
    this.updateUserIdsInputs() // Legacy support

    // Legacy support for specific actions
    switch (action) {
      case "assign_role":
        if (this.hasAssignRoleModalTarget) {
          this.showAssignRoleModal()
          return
        }
        break
      case "export":
        this.performExport(formUrl || '/admin/users/bulk_action')
        return
    }

    // New generic handling
    if (formUrl && this.hasConfirmFormTarget) {
      this.confirmFormTarget.action = formUrl
    }
    
    if (this.hasMethodInputTarget) {
      this.methodInputTarget.value = method
    }
    
    // Legacy support for actionInput
    if (this.hasActionInputTarget) {
      this.actionInputTarget.value = action
    }

    this.showConfirmModal(action, isDangerous)
  }

  // Legacy method support
  showAssignRoleModal() {
    if (this.hasAssignRoleModalTarget) {
      this.assignRoleModalTarget.classList.remove("hidden")
    }
  }

  showConfirmModal(action, isDangerous) {
    // Legacy support for specific action messages
    const titles = {
      suspend: "Suspend Users",
      activate: "Activate Users", 
      remove_role: "Remove Role",
      delete: "Delete Users",
      move_blacklist: "Move to Blacklist",
      move_greylist: "Move to Grey List"
    }
    
    const messages = {
      suspend: `Are you sure you want to suspend ${this.selectedIds.size} selected users?`,
      activate: `Are you sure you want to activate ${this.selectedIds.size} selected users?`,
      remove_role: `Are you sure you want to remove roles from ${this.selectedIds.size} selected users?`,
      delete: `Are you sure you want to delete ${this.selectedIds.size} selected users? This action cannot be undone.`,
      move_blacklist: `Are you sure you want to move ${this.selectedIds.size} selected devices to the Blacklist? Any devices in Paired status will have their IMSI pairs removed.`,
      move_greylist: `Are you sure you want to move ${this.selectedIds.size} selected devices to the Grey List? Any devices in Paired status will have their IMSI pairs removed.`
    }

    // Use legacy titles/messages if available, otherwise generate generic ones
    const actionLabel = action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    this.confirmTitleTarget.textContent = titles[action] || `Confirm ${actionLabel}`
    
    if (this.hasConfirmMessageTarget) {
      this.confirmMessageTarget.textContent = messages[action] || "Are you sure you want to perform this action?"
    }
    
    // Update button styling for dangerous actions
    if (isDangerous) {
      this.confirmButtonTarget.className = "px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
    } else {
      this.confirmButtonTarget.className = "px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
    }
    
    // Clear any existing dynamic fields
    if (this.hasDynamicFieldsTarget) {
      this.dynamicFieldsTarget.innerHTML = ''
      
      // Add status field for move actions
      if (action.startsWith('move_')) {
        const statusMap = {
          move_blacklist: 'B',
          move_greylist: 'G'
        }
        const statusInput = document.createElement('input')
        statusInput.type = 'hidden'
        statusInput.name = 'new_status'
        statusInput.value = statusMap[action]
        this.dynamicFieldsTarget.appendChild(statusInput)
      }
    }
    
    this.confirmModalTarget.classList.remove("hidden")
  }

  closeModal() {
    if (this.hasConfirmModalTarget) {
      this.confirmModalTarget.classList.add("hidden")
    }
    // Legacy support
    if (this.hasAssignRoleModalTarget) {
      this.assignRoleModalTarget.classList.add("hidden")
    }
  }

  performExport(formUrl) {
    // Create a form to submit for export
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = formUrl
    
    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    const csrfInput = document.createElement('input')
    csrfInput.type = 'hidden'
    csrfInput.name = 'authenticity_token'
    csrfInput.value = csrfToken
    form.appendChild(csrfInput)
    
    // Add IDs
    this.selectedIds.forEach(id => {
      const idInput = document.createElement('input')
      idInput.type = 'hidden'
      idInput.name = this.getIdFieldName()
      idInput.value = id
      form.appendChild(idInput)
    })
    
    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)
  }

  updateToolbar() {
    const count = this.selectedIds.size
    this.selectedCountTarget.textContent = count
    
    if (count > 0) {
      this.toolbarTarget.classList.remove("hidden")
    } else {
      this.toolbarTarget.classList.add("hidden")
    }
  }

  updateIdsInput() {
    if (!this.hasIdsInputTarget) return
    
    const ids = Array.from(this.selectedIds)
    
    // Update the hidden input with comma-separated IDs
    this.idsInputTarget.value = ids.join(',')
    
    // Also create individual hidden inputs
    const form = this.confirmFormTarget
    
    // Remove any existing ID inputs
    const existingInputs = form.querySelectorAll(`input[name="${this.getIdFieldName()}"]:not([data-bulk-operations-target])`)
    existingInputs.forEach(input => input.remove())
    
    // Add new hidden inputs for each ID
    ids.forEach(id => {
      const newInput = document.createElement('input')
      newInput.type = 'hidden'
      newInput.name = this.getIdFieldName()
      newInput.value = id
      form.appendChild(newInput)
    })
  }

  // Legacy support for user IDs
  updateUserIdsInputs() {
    if (!this.hasUserIdsInputTargets) return
    
    const userIds = Array.from(this.selectedIds)
    
    // Update all user IDs inputs
    this.userIdsInputTargets.forEach(input => {
      // Clear the placeholder input value
      input.value = userIds.join(',')
      
      // If there's a form, also create individual hidden inputs
      const form = input.closest('form')
      if (form) {
        // Remove any existing user_ids inputs except the placeholder
        const existingInputs = form.querySelectorAll('input[name="user_ids[]"]:not([data-bulk-operations-target])')
        existingInputs.forEach(existingInput => {
          existingInput.remove()
        })
        
        // Add new hidden inputs for each user ID
        userIds.forEach(userId => {
          const newInput = document.createElement('input')
          newInput.type = 'hidden'
          newInput.name = 'user_ids[]'
          newInput.value = userId
          form.appendChild(newInput)
        })
      }
    })
  }

  getIdFieldName() {
    // Get the name attribute from the idsInput target
    if (this.hasIdsInputTarget) {
      const placeholder = this.idsInputTarget
      return placeholder.name || 'ids[]'
    }
    return 'ids[]'
  }
}